import type { Annotation } from "@/lib/api";

/** Pure geometry for the infinite canvas (plan F7). All framework-free + exported
 * so the roam-bounds / camera math is unit-tested without any DOM or Fabric.
 *
 * An infinite canvas stores ink in the SAME tile-normalized coordinates a page
 * does — x relative to the tile width, y to its height — but UNBOUNDED: negative
 * and >1 are valid, so a stroke drawn 20,000 px from the origin normalizes to a
 * coordinate far outside [0,1]. Scene coordinates here are tile PIXELS (normalized
 * × tile size), the frame the single page-0 PageEditor and the ViewportController
 * camera both live in. */

/** The tile the infinite canvas normalizes against — the SAME US-Letter reference
 * a paged canvas uses (CanvasView BASE_W/BASE_H), so a note can be re-laid-out or
 * exported through the same crop-to-content path. Mirrored here (not imported) to
 * keep this pure module free of the React component. */
export const TILE_W = 1700;
export const TILE_H = 2200;

export interface Extent {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

/** Ink extents in TILE-pixel scene coords for a page's annotations (their stored
 * geometry is tile-normalized). Returns null when there is nothing to bound (so an
 * empty canvas falls back to the tile as its reference content). */
export function inkExtents(annotations: Annotation[], tileW = TILE_W, tileH = TILE_H): Extent | null {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  let seen = false;
  const acc = (x: number, y: number) => {
    seen = true;
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
  };
  for (const a of annotations) {
    if (a.points) for (const p of a.points) acc(p.x * tileW, p.y * tileH);
    if (a.rect) {
      acc(a.rect.x * tileW, a.rect.y * tileH);
      acc((a.rect.x + a.rect.w) * tileW, (a.rect.y + a.rect.h) * tileH);
    }
  }
  return seen ? { minX, minY, maxX, maxY } : null;
}

/** Union of two extents (either may be null). */
export function unionExtent(a: Extent | null, b: Extent | null): Extent | null {
  if (!a) return b;
  if (!b) return a;
  return {
    minX: Math.min(a.minX, b.minX),
    minY: Math.min(a.minY, b.minY),
    maxX: Math.max(a.maxX, b.maxX),
    maxY: Math.max(a.maxY, b.maxY),
  };
}

/** The roam bounds (scene-space content box) for the infinite canvas: the union of
 * the ink extents and the currently-visible scene rect, padded by a generous
 * margin. Unioning with the VIEW is what stops the camera being trapped — every
 * pan expands the frontier by a margin, so you can always roam a viewport further —
 * while still keeping the scrollbars tight around real content. Returns
 * {ox,oy,w,h} for {@link ViewportController.setContentBounds}. */
export function roamBounds(
  ink: Extent | null,
  view: Extent,
  marginX: number,
  marginY: number,
): { ox: number; oy: number; w: number; h: number } {
  const u = unionExtent(ink, view) ?? view;
  const ox = Math.max(u.minX - marginX, leftRoamLimit(ink));
  const oy = Math.max(u.minY - marginY, topRoamLimit(ink));
  return {
    ox,
    oy,
    w: u.maxX - ox + marginX,
    h: u.maxY - oy + marginY,
  };
}

/** Scene x/y where the writable surface begins. The infinite canvas is unbounded
 * to the right and down, but NOT up or left: it needs a corner. Without one
 * there is no "beginning" to return to — you pan forever through identical blank
 * space with your work somewhere off the edge, and nothing on screen tells you
 * which way is back. The top-left corner is the orientation. */
export const WRITABLE_LEFT = 0;
export const WRITABLE_TOP = 0;

/** How far past the left edge you may still roam, in scene px — enough grey to
 * see that the paper has an edge, not enough to get lost in. */
export const LEFT_GUTTER = 240;

/** The leftmost scene x the camera may reach. Existing ink further left than the
 * gutter (written before there was a bound, or dragged there) still has to be
 * reachable, or this change would strand it off-screen — so the limit yields to
 * whatever is actually out there. */
export function leftRoamLimit(ink: Extent | null): number {
  const gutter = WRITABLE_LEFT - LEFT_GUTTER;
  return ink ? Math.min(gutter, ink.minX - LEFT_GUTTER) : gutter;
}

/** The topmost scene y the camera may reach — same rule as the left. */
export function topRoamLimit(ink: Extent | null): number {
  const gutter = WRITABLE_TOP - LEFT_GUTTER;
  return ink ? Math.min(gutter, ink.minY - LEFT_GUTTER) : gutter;
}

/** Is this scene point on the paper (as opposed to the grey margin beside or
 * above it)? */
export function isWritableX(x: number): boolean {
  return x >= WRITABLE_LEFT;
}

export function isWritablePoint(x: number, y: number): boolean {
  return x >= WRITABLE_LEFT && y >= WRITABLE_TOP;
}

/** Pull a scene coordinate onto the paper. A stroke that starts on the page and
 * runs off an edge is clamped rather than dropped — losing the whole stroke
 * because its tail wandered would be worse than a flattened tail. */
export function clampWritableX(x: number): number {
  return x < WRITABLE_LEFT ? WRITABLE_LEFT : x;
}

export function clampWritableY(y: number): number {
  return y < WRITABLE_TOP ? WRITABLE_TOP : y;
}

/** The camera (tx,ty) that centres a scene point (scx,scy) in the viewport at
 * `scale`. Fabric maps scene→screen as `s·coord + t`, so holding the centre gives
 * `t = viewport-centre − s·coord`. Used to seat an empty canvas on the tile origin. */
export function centerCamera(
  scx: number,
  scy: number,
  viewW: number,
  viewH: number,
  scale: number,
): { tx: number; ty: number } {
  return { tx: viewW / 2 - scale * scx, ty: viewH / 2 - scale * scy };
}
